/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { Input as AntInput, InputRef } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import Text from 'src/components/ts-components/Text'
import { useRef, useEffect, useCallback } from 'react'

export interface Option {
  id: string
  label: string
}

interface OptionEditorProps {
  options: Option[]
  onChange: (options: Option[]) => void
}

const generateId = (): string => {
  return `option_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

const OptionEditor = ({ options, onChange }: OptionEditorProps) => {
  const inputRefs = useRef<{ [key: string]: InputRef | null }>({})
  const lastAddedOptionId = useRef<string | null>(null)

  const handleOptionChange = (optionId: string, label: string) => {
    const updatedOptions = options.map((option) =>
      option.id === optionId ? { ...option, label } : option
    )
    onChange(updatedOptions)
  }

  const handleDeleteOption = (optionId: string) => {
    if (options.length > 1) {
      const updatedOptions = options.filter((option) => option.id !== optionId)
      onChange(updatedOptions)
    }
  }

  const handleAddOption = useCallback(() => {
    const newOption: Option = {
      id: generateId(),
      label: '',
    }
    lastAddedOptionId.current = newOption.id
    onChange([...options, newOption])
  }, [options, onChange])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, optionId: string) => {
      if (e.key === 'Enter') {
        e.preventDefault()

        // Find the current option index
        const currentIndex = options.findIndex(
          (option) => option.id === optionId
        )
        const isLastOption = currentIndex === options.length - 1

        // Only add new option if this is the last option and it has content
        if (isLastOption && e.currentTarget.value.trim()) {
          handleAddOption()
        }
      }
    },
    [options, handleAddOption]
  )

  // Auto-focus on newly added option
  useEffect(() => {
    if (
      lastAddedOptionId.current &&
      inputRefs.current[lastAddedOptionId.current]
    ) {
      const input = inputRefs.current[lastAddedOptionId.current]
      if (input) {
        // Use setTimeout to ensure the DOM has updated
        setTimeout(() => {
          input.focus()
        }, 0)
      }
      lastAddedOptionId.current = null
    }
  }, [options])

  const setInputRef = useCallback(
    (optionId: string) => (ref: InputRef | null) => {
      inputRefs.current[optionId] = ref
    },
    []
  )

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 8px;
      `}
    >
      <Text variant='paragraphSmall' weight='medium' color={colors.WHITE}>
        Options
      </Text>
      {options.map((option, index) => (
        <div
          key={option.id}
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
          `}
        >
          <div
            css={css`
              flex: 1;
            `}
          >
            <AntInput
              ref={setInputRef(option.id)}
              value={option.label}
              onChange={(e) => handleOptionChange(option.id, e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, option.id)}
              placeholder={`Option ${index + 1}`}
              css={css`
                font-size: 14px !important;
                height: 40px !important;
                border-radius: 8px;
                border: 1px solid ${colors.SECONDARY_100};
                background: ${colors.SECONDARY_200};
                color: ${colors.WHITE};
              `}
            />
          </div>
          {options.length > 1 && (
            <button
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                width: 32px;
                height: 32px;
                border: none;
                background: transparent;
                cursor: pointer;
                border-radius: 4px;
                &:hover {
                  background: rgba(255, 255, 255, 0.1);
                }
              `}
              onClick={() => handleDeleteOption(option.id)}
            >
              <DeleteOutlined
                css={css`
                  color: ${colors.ERROR_300};
                  font-size: 16px;
                `}
              />
            </button>
          )}
        </div>
      ))}
      <button
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px;
          border: 1px dashed ${colors.NEUTRAL_50};
          background: transparent;
          border-radius: 8px;
          cursor: pointer;
          &:hover {
            border-color: ${colors.PRIMARY};
            background: rgba(255, 255, 255, 0.05);
          }
        `}
        onClick={handleAddOption}
      >
        <PlusOutlined
          css={css`
            color: ${colors.NEUTRAL_50};
            font-size: 14px;
          `}
        />
        <Text
          variant='paragraphSmall'
          weight='regular'
          color={colors.NEUTRAL_50}
        >
          Add option
        </Text>
      </button>
    </div>
  )
}

export default OptionEditor
